import React, { useCallback, useEffect, useState } from "react";

import { useNavigate } from "react-router-dom";

import Layout from "../components/Layout";
import { API } from "../api";

import { FiClipboard, FiSearch, FiRefreshCw, FiUserPlus } from "react-icons/fi";

import { toasts } from "../components/AppToast";
export default function ManageMultipleBookingDetails() {
  // NAVIGATION

  const navigate = useNavigate();

  // DATA

  const [data, setData] = useState([]);

  const [loading, setLoading] = useState(false);

  // FILTERS

  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  /*
  | Selected value in dropdown
  */

  const [selectedvisitsAbove, setSelectedvisitsAbove] = useState(1);

  /*
  | Applied value
  |
  | Search button applies this value.
  */

  const [appliedMinBookingDays, setAppliedMinBookingDays] = useState(1);

  // PAGINATION

  const [page, setPage] = useState(1);

  const [limit] = useState(10);

  const [total, setTotal] = useState(0);

  const [totalPages, setTotalPages] = useState(0);

  // FORMAT DATE

  const formatDate = (date) => {
    if (!date) return "";
  
    const d = new Date(date);
  
    const day = String(d.getDate()).padStart(2, "0");
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const year = d.getFullYear();
  
    // For <input type="date">
    return `${year}-${month}-${day}`;
  };

  const formatDateForAPI = (date) => {
    if (!date) return "";
  
    const [year, month, day] = date.split("-");
  
    return `${day}-${month}-${year}`;
  };

  // DEFAULT DATE
  // LAST 900 DAYS

  const setDefaultDates = useCallback(() => {
    const today = new Date();

    const startDate = new Date(today);

    startDate.setDate(startDate.getDate() - 900);

    setFromDate(formatDate(startDate));

    setToDate(formatDate(today));
  }, []);

  // FETCH BOOKINGS

  const fetchBookings = useCallback(
    async (pageNumber = 1) => {
      try {
        setLoading(true);
        const apiFromDate = formatDateForAPI(fromDate);
      const apiToDate = formatDateForAPI(toDate);

      // console.log("Frontend Date:", fromDate, toDate);
      // console.log("API Date:", apiFromDate, apiToDate);

        const response = await API.get("/multiple-bookings", {
          params: {
            fromDate: apiFromDate,
            toDate: apiToDate,
            selectedvisitsAbove: appliedMinBookingDays,
            page: pageNumber,
            limit,
          },
        });

        // RESPONSE CHECK

        if (!response.data?.success) {
          toasts.error(
            response.data?.message || "Failed to load multiple bookings"
          );

          return;
        }

        const responseData = response.data;

        // DATA

        setData(responseData.data || []);

        // TOTAL RECORDS

        const totalRecords =
          Number(responseData?.pagination?.totalRecords) || 0;

        setTotal(totalRecords);

        // TOTAL PAGES

        const pages = Number(responseData?.pagination?.totalPages) || 0;

        setTotalPages(pages);

        // CURRENT PAGE

        setPage(Number(responseData?.pagination?.page) || pageNumber);
      } catch (error) {
        console.error("LOAD MULTIPLE BOOKINGS ERROR:", error);

        toasts.error(
          error.response?.data?.message || "Failed to load multiple bookings"
        );
      } finally {
        setLoading(false);
      }
    },
    [fromDate, toDate, appliedMinBookingDays, limit]
  );

  // INITIAL DATE

  useEffect(() => {
    setDefaultDates();
  }, [setDefaultDates]);

  // LOAD DATA

  useEffect(() => {
    if (fromDate && toDate) {
      fetchBookings(page);
    }
  }, [fromDate, toDate, page, fetchBookings]);

  // SEARCH

  const handleSearch = () => {
    if (!fromDate || !toDate) {
      toasts.error("Please select From Date and To Date");
      return;
    }
  
    if (fromDate > toDate) {
      toasts.error("From Date cannot be greater than To Date");
      return;
    }
  
    setAppliedMinBookingDays(Number(selectedvisitsAbove) || 1);
    setPage(1);
  };

  // RESET

  const handleReset = () => {
    const today = new Date();

    const startDate = new Date(today);

    startDate.setDate(startDate.getDate() - 900);

    setFromDate(formatDate(startDate));

    setToDate(formatDate(today));

    setSelectedvisitsAbove(1);

    setAppliedMinBookingDays(1);

    setTotal(0);

    setTotalPages(0);

    setPage(1);
  };

  // VIEW BOOKING

  const handleView = (bookingId) => {
    if (!bookingId) {
      toasts.error("Booking ID not found");

      return;
    }

    navigate(`/manage-regular-clients/edit/${bookingId}`);
  };

  // ADD REGULAR CLIENT

  const handleAddRegularClient = (bookingId) => {
    if (!bookingId) {
      toasts.error("Booking ID not found");

      return;
    }

    navigate("/manage-regular-clients/add", {
      state: {
        bookingId: bookingId,
      },
    });
  };

  // PREVIOUS PAGE

  const handlePreviousPage = () => {
    if (page <= 1 || loading) {
      return;
    }

    setPage((previous) => Math.max(1, previous - 1));
  };

  // NEXT PAGE

  const handleNextPage = () => {
    if (page >= totalPages || loading) {
      return;
    }

    setPage((previous) => Math.min(totalPages, previous + 1));
  };

  // RENDER

  return (
    <Layout activePage="Manage Multiple Booking Details">
    

      <main className="dashboard-main">
        <div className="table-card">
          {/*  HEADER */}

          <div className="table-card-head">
            <h3 className="table-card-title d-flex align-items-center">
              <FiClipboard size={26} className="me-2" />
              <span>Manage Multiple Booking Details</span>
              <span className="rec-count ms-1">{total} records</span>
            </h3>

            <div className="d-flex align-items-center gap-2">
              <button
                type="button"
                className="um-btn-secondary btn-sm"
                onClick={() => fetchBookings(page)}
                disabled={loading}
              >
                <FiRefreshCw className={loading ? "me-1 spinner" : "me-1"} />
                Refresh
              </button>
            </div>
          </div>

          {/*  FILTER */}

          <div className="p-3 border-bottom bg-light bg-opacity-25">
            <div className="row g-3 align-items-end">
              {/*      FROM DATE
              ================================================== */}

              <div className="col-md-3">
                <label className="form-label fw-semibold small">
                  From Date
                </label>

                <input
                 type="date"
                  className="form-control"
                  placeholder="DD-MM-YYYY"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                />
              </div>

              {/*      TO DATE
              ================================================== */}

              <div className="col-md-3">
                <label className="form-label fw-semibold small">To Date</label>

                <input
                  type="date"
                  className="form-control"
                  placeholder="DD-MM-YYYY"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                />
              </div>

              {/*      MIN BOOKING DAYS
              ================================================== */}

              <div className="col-md-3">
                <label className="form-label fw-semibold small">
                  Min. Booking Days
                </label>

                <select
                  className="form-select season-input"
                  value={selectedvisitsAbove}
                  onChange={(e) =>
                    setSelectedvisitsAbove(Number(e.target.value))
                  }
                >
                  {Array.from({ length: 20 }, (_, index) => index + 1).map(
                    (value) => (
                      <option key={value} value={value}>
                        {value}
                      </option>
                    )
                  )}
                </select>
              </div>

              {/*      BUTTONS
              ================================================== */}

              <div className="col-md-3">
                <div className="d-flex gap-2">
                  {/* SEARCH */}

                  <button
                    type="button"
                    className="um-btn-primary flex-grow-1"
                    disabled={loading}
                    onClick={handleSearch}
                  >
                    {loading ? (
                      <>
                        <span
                          className="spinner me-1"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        Loading...
                      </>
                    ) : (
                      <>
                        <FiSearch className="me-1" />
                        Search
                      </>
                    )}
                  </button>

                  {/* RESET */}

                  <button
                    type="button"
                    className="um-btn-secondary flex-grow-1"
                    disabled={loading}
                    onClick={handleReset}
                  >
                    <FiRefreshCw className="me-1" />
                    Reset
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/*  TABLE */}

          <div>
            <table
              className="table table-striped table-hover table-bordered align-middle mb-0"
              style={{
                tableLayout: "fixed",
                width: "100%",
                fontSize: "12.5px",
              }}
            >
              {/*      TABLE HEADER*/}

              <thead className="table-light">
                <tr className="um-table-head text-center">
                  <th style={{ width: "6%" }} className="text-center p-2">
                    #
                  </th>

                  <th style={{ width: "20%" }} className="text-center p-2">
                    Email ID
                  </th>

                  <th style={{ width: "14%" }} className="text-center p-2">
                    No Of Bookings
                  </th>

                  <th style={{ width: "16%" }} className="text-center p-2">
                    No Of Days Booked
                  </th>

                  <th style={{ width: "24%" }} className="text-center p-2">
                    Booking Ref. Nos
                  </th>

                  <th style={{ width: "20%" }} className="text-center p-2">
                    Views
                  </th>
                </tr>
              </thead>

              {/*      TABLE BODY */}

              <tbody>
                {/*        LOADING */}

                {loading ? (
                  Array.from({ length: 6 }).map((_, i) => (
                    <tr key={i}>
                      {Array.from({ length: 6 }).map((_, j) => (
                        <td key={j} className="text-center p-2">
                          <div className="skeleton" />
                        </td>
                      ))}
                    </tr>
                  ))
                ) : data.length === 0 ? (
                  /*          NO DATA */

                  <tr>
                    <td colSpan="6" className="text-center py-5">
                      <div className="empty-icon">
                        <FiSearch size={22} />
                      </div>
                      <div className="empty-title">No records Available</div>
                    </td>
                  </tr>
                ) : (
                  /*          DATA*/

                  data.map((row, index) => {
                    const firstBookingId = row.booking_ids?.[0] || null;

                    return (
                      <tr key={row.online_emailid || index} className="um-row">
                        {/* SERIAL NUMBER */}

                        <td className="text-center p-2 text-muted">
                          {row.serial_no || (page - 1) * limit + index + 1}
                        </td>

                        {/* EMAIL */}

                        <td
                          className="text-center p-2"
                          style={{ wordBreak: "break-all" }}
                        >
                          <div>{row.online_emailid}</div>

                          {row.online_clientid && (
                            <small className="text-success fw-semibold mt-1 d-block">
                              ({row.online_clientid})
                            </small>
                          )}
                        </td>

                        {/* NUMBER OF BOOKINGS */}

                        <td className="text-center p-2">
                          <div className="status-wrap justify-content-center m-0">
                            <span className="badge badge-success">
                              {row.no_of_bookings}
                            </span>
                          </div>
                        </td>

                        {/* NUMBER OF DAYS */}

                        <td className="text-center p-2">
                          <span className="badge badge-success">
                            {row.no_of_days_booked}
                          </span>
                        </td>

                        {/* BOOKING REFERENCES */}

                        <td
                          className="text-center p-2"
                          style={{ wordBreak: "break-word" }}
                        >
                          {row.booking_reference_nos?.length > 0 ? (
                            <div className="d-flex flex-column align-items-center">
                              {row.booking_reference_nos.map(
                                (reference, refIndex) => {
                                  const bookingId = row.booking_ids?.[refIndex];

                                  return (
                                    <button
                                      key={`${reference}-${refIndex}`}
                                      type="button"
                                      className="btn btn-sm p-0 mb-1"
                                      onClick={() => handleView(bookingId)}
                                    >
                                      {reference}
                                    </button>
                                  );
                                }
                              )}
                            </div>
                          ) : (
                            "—"
                          )}
                        </td>

                        {/* VIEW / CLIENT */}

                        <td className="p-2 text-center">
                          <div className="action-buttons d-flex justify-content-center gap-1">
                            {row.online_clientid ? (
                              <div className="status-wrap justify-content-center m-0">
                                <span className="badge badge-success">
                                  {row.online_clientid}
                                </span>
                              </div>
                            ) : (
                              firstBookingId && (
                                <button
                                  type="button"
                                  className="um-btn-primary"
                                  onClick={() =>
                                    handleAddRegularClient(firstBookingId)
                                  }
                                  title="Add Client"
                                >
                                  <span className="um-add-icon">
                                    {" "}
                                    <FiUserPlus className="me-1" />{" "}
                                  </span>
                                  Add Client
                                </button>
                              )
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/*  PAGINATION */}

          {!loading && totalPages > 0 && total > 0 && (
            <div className="pagination">
              <span className="pagin-info">
                Showing {(page - 1) * limit + 1}–{Math.min(page * limit, total)}{" "}
                of {total} records
              </span>

              <div className="d-flex align-items-center gap-2">
                <button
                  type="button"
                  className="pg-btn"
                  disabled={page === 1 || loading}
                  onClick={handlePreviousPage}
                >
                  ← Prev
                </button>

                <span className="pagin-info">
                  Page {page} / {totalPages || 1}
                </span>

                <button
                  type="button"
                  className="pg-btn"
                  disabled={page >= totalPages || loading}
                  onClick={handleNextPage}
                >
                  Next →
                </button>
              </div>
            </div>
          )}
        </div>
      </main>
 
    </Layout>
  );
}
